import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Stack, Card, Chip, Skeleton, Alert,
  alpha, useTheme, LinearProgress, FormControl, InputLabel, Select, MenuItem,
  Button, TextField, IconButton, CircularProgress,
} from '@mui/material';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from '../components/common/modal';
import {
  Target, Store, ShoppingCart, MapPin, Tag, TrendingUp, Lightbulb,
  Search, Sparkles, Users as UsersIcon, Briefcase, ShoppingBag,
  MessageSquare, Scale, Settings as SettingsIcon, Zap, Megaphone,
} from 'lucide-react';
import axios from '../utils/axios';
import DashboardSectionHeader from '../components/DashboardSectionHeader';
import KpiTile from '../components/charts/KpiTile';

const RANGES = [
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
  { label: '180d', value: 180 },
  { label: 'All', value: 0 },
];

const gridItemSx = { display: 'flex', '& > *': { flex: 1, width: '100%', minWidth: 0 } };

// Small labelled metric pill used inside opportunity cards.
const StatPill = ({ icon, label, value, color }) => {
  const theme = useTheme();
  const c = color || theme.palette.text.secondary;
  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{
        px: 1, py: 0.5, borderRadius: 1.5,
        bgcolor: alpha(c, 0.10), color: c, flexShrink: 0,
      }}
    >
      {icon}
      <Typography sx={{ fontSize: 12, fontWeight: 800, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, fontWeight: 600, opacity: 0.85, lineHeight: 1 }}>{label}</Typography>
    </Stack>
  );
};

const OpportunityCard = ({ rank, accent, item, type }) => {
  const theme = useTheme();
  const isSeller = type === 'seller';
  return (
    <Box
      sx={{
        p: 2, borderRadius: 2.5,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        transition: 'all 160ms ease',
        '&:hover': { borderColor: alpha(accent, 0.5), boxShadow: `0 6px 18px ${alpha(accent, 0.12)}` },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 28, height: 28, borderRadius: 1.5, flexShrink: 0,
            bgcolor: alpha(accent, 0.14), color: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 13,
          }}
        >
          {rank}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Chip
              size="small"
              icon={<MapPin size={13} />}
              label={item.city}
              sx={{ fontWeight: 700, bgcolor: alpha(accent, 0.10), color: accent, '& .MuiChip-icon': { color: accent } }}
            />
            <Chip
              size="small"
              icon={<Tag size={13} />}
              label={item.category}
              variant="outlined"
              sx={{ fontWeight: 700, borderColor: theme.palette.divider, color: 'text.secondary', '& .MuiChip-icon': { color: 'text.disabled' } }}
            />
            {item.channel && (
              <Chip
                size="small"
                icon={<Megaphone size={13} />}
                label={item.channel}
                variant="outlined"
                sx={{ fontWeight: 700, borderColor: alpha(accent, 0.35), color: accent, '& .MuiChip-icon': { color: accent } }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
            <StatPill icon={<MessageSquare size={13} />} value={item.demand} label="demand" color={theme.palette.error.main} />
            <StatPill icon={<Briefcase size={13} />} value={item.sellers} label="sellers" color={theme.palette.primary.main} />
            <StatPill icon={<ShoppingBag size={13} />} value={item.products} label="products" color={theme.palette.info.main} />
          </Stack>

          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1, lineHeight: 1.5 }}>
            {item.reason}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="flex-start"
            sx={{ p: 1.25, borderRadius: 1.5, bgcolor: alpha(accent, 0.06) }}>
            <Box sx={{ color: accent, mt: '2px', flexShrink: 0 }}>
              {isSeller ? <Store size={15} /> : <ShoppingCart size={15} />}
            </Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: theme.palette.mode === 'dark' ? 'text.primary' : alpha(accent, 0.95), lineHeight: 1.5 }}>
              {item.action}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const PlanColumn = ({ title, subtitle, icon, accent, items, type, emptyText }) => {
  return (
    <Card sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 44, height: 44, borderRadius: 2, flexShrink: 0,
            bgcolor: alpha(accent, 0.14), color: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>{title}</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 500 }}>{subtitle}</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Chip
          label={`${items.length}`}
          size="small"
          sx={{ fontWeight: 800, bgcolor: alpha(accent, 0.12), color: accent }}
        />
      </Stack>

      {items.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center', color: 'text.disabled' }}>
          <Sparkles size={28} style={{ opacity: 0.5 }} />
          <Typography sx={{ mt: 1, fontSize: 13, fontWeight: 600 }}>{emptyText}</Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {items.map((item, i) => (
            <OpportunityCard key={`${item.city}-${item.category}-${i}`} rank={i + 1} accent={accent} item={item} type={type} />
          ))}
        </Stack>
      )}
    </Card>
  );
};

const CoverageList = ({ title, icon, accent, rows, nameKey, maxSupply }) => {
  const theme = useTheme();
  return (
    <Card sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, height: '100%' }}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
        <Box sx={{ color: accent, display: 'flex' }}>{icon}</Box>
        <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>{title}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
        Lowest coverage first — prime spots to seed inventory.
      </Typography>
      {rows.length === 0 ? (
        <Typography sx={{ color: 'text.disabled', fontSize: 13, py: 3, textAlign: 'center' }}>No data yet.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((r, i) => (
            <Box key={`${r[nameKey]}-${i}`}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 700, minWidth: 0, mr: 1 }}>{r[nameKey]}</Typography>
                <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                  <Chip size="small" label={`${r.supply} supply`} sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: alpha(accent, 0.12), color: accent }} />
                  <Chip size="small" label={`${r.demand} demand`} sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: alpha(theme.palette.error.main, 0.10), color: theme.palette.error.main }} />
                </Stack>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={maxSupply > 0 ? Math.min(100, (r.supply / maxSupply) * 100) : 0}
                sx={{
                  height: 6, borderRadius: 3,
                  bgcolor: alpha(theme.palette.grey[500], 0.14),
                  '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: accent },
                }}
              />
            </Box>
          ))}
        </Stack>
      )}
    </Card>
  );
};

// Counter field with − / + steppers for the campaign-idea counts.
const CountField = ({ icon, accent, label, helper, value, min, max, onChange, hint }) => {
  const theme = useTheme();
  const clamp = (n) => Math.min(max, Math.max(min, n));
  return (
    <Box
      sx={{
        p: 2, borderRadius: 2.5, height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(accent, 0.04),
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, flexShrink: 0, bgcolor: alpha(accent, 0.14), color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>{label}</Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{helper}</Typography>
        </Box>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton
          aria-label="Decrease"
          size="small"
          onClick={() => onChange(clamp(Number(value) - 1))}
          disabled={Number(value) <= min}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5, fontWeight: 800 }}
        >
          −
        </IconButton>
        <TextField
          size="small"
          type="number"
          value={value}
          onChange={(e) => onChange(clamp(parseInt(e.target.value, 10) || min))}
          inputProps={{ min, max, style: { textAlign: 'center', fontWeight: 800, fontSize: 18 } }}
          sx={{ width: 84 }}
        />
        <IconButton
          aria-label="Increase"
          size="small"
          onClick={() => onChange(clamp(Number(value) + 1))}
          disabled={Number(value) >= max}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5, fontWeight: 800 }}
        >
          +
        </IconButton>
        <Typography sx={{ fontSize: 11.5, color: 'text.disabled', ml: 0.5 }}>{hint || `of ${max} max`}</Typography>
      </Stack>
    </Box>
  );
};

const SettingsDialog = ({ open, onClose, settings, onSaved }) => {
  const theme = useTheme();
  const limits = settings?.limits || { min: 1, max: 12, minThreshold: 0, maxThreshold: 1000 };
  const minT = limits.minThreshold ?? 0;
  const maxT = limits.maxThreshold ?? 1000;
  const [sellerCount, setSellerCount] = useState(4);
  const [buyerCount, setBuyerCount] = useState(4);
  const [sellerMinSellers, setSellerMinSellers] = useState(0);
  const [buyerMinDemand, setBuyerMinDemand] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSellerCount(settings?.seller_campaign_count ?? 4);
      setBuyerCount(settings?.buyer_campaign_count ?? 4);
      setSellerMinSellers(settings?.seller_min_sellers ?? 0);
      setBuyerMinDemand(settings?.buyer_min_demand ?? 0);
      setError('');
    }
  }, [open, settings]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put('/admin/marketing/settings', {
        seller_campaign_count: sellerCount,
        buyer_campaign_count: buyerCount,
        seller_min_sellers: sellerMinSellers,
        buyer_min_demand: buyerMinDemand,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminDialog open={open} onClose={!saving ? onClose : undefined} maxWidth="sm">
      <AdminDialogHeader
        title="Marketing Step Settings"
        subtitle="Control idea density per side and minimum-quantity thresholds"
        icon={SettingsIcon}
        onClose={!saving ? onClose : undefined}
      />
      <AdminDialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Response limits — how many ideas to surface per side */}
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.6, textTransform: 'uppercase', mb: 1.25 }}>
          Ideas to show
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountField
              icon={<Store size={18} />}
              accent={theme.palette.primary.main}
              label="Seller campaign ideas"
              helper="Recruit-seller suggestions"
              value={sellerCount}
              min={limits.min}
              max={limits.max}
              onChange={setSellerCount}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountField
              icon={<ShoppingCart size={18} />}
              accent={theme.palette.secondary.main}
              label="Buyer campaign ideas"
              helper="Attract-buyer suggestions"
              value={buyerCount}
              min={limits.min}
              max={limits.max}
              onChange={setBuyerCount}
            />
          </Grid>
        </Grid>

        {/* Minimum-quantity thresholds */}
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.6, textTransform: 'uppercase', mt: 2.5, mb: 1.25 }}>
          Minimum quantity to qualify
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountField
              icon={<Briefcase size={18} />}
              accent={theme.palette.primary.main}
              label="Min. existing sellers"
              helper="Only show seller ideas with ≥ this many sellers"
              value={sellerMinSellers}
              min={minT}
              max={maxT}
              onChange={setSellerMinSellers}
              hint={sellerMinSellers === 0 ? 'no minimum' : 'sellers'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CountField
              icon={<MessageSquare size={18} />}
              accent={theme.palette.secondary.main}
              label="Min. buyer demand"
              helper="Only show buyer ideas with ≥ this much demand"
              value={buyerMinDemand}
              min={minT}
              max={maxT}
              onChange={setBuyerMinDemand}
              hint={buyerMinDemand === 0 ? 'no minimum' : 'enquiries'}
            />
          </Grid>
        </Grid>
      </AdminDialogContent>
      <AdminDialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving} sx={{ fontWeight: 600 }}>Cancel</Button>
        <Button
          onClick={save}
          variant="contained"
          color="primary"
          disabled={saving}
          startIcon={saving && <CircularProgress size={16} color="inherit" />}
          sx={{ px: 3, borderRadius: 2, fontWeight: 700 }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </AdminDialogActions>
    </AdminDialog>
  );
};

const formatAgo = (iso) => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// Gemini-generated plan. Only ever filled by the admin clicking the AI button
// (or the stored last result) — the page never calls Gemini on its own.
const AiSuggestionsPanel = ({ ai, loading }) => {
  const theme = useTheme();
  const accent = '#a855f7';

  if (loading) {
    return (
      <Card sx={{ p: { xs: 2.5, md: 3 }, mb: 2.5, borderRadius: 3, border: `1px solid ${alpha(accent, 0.3)}` }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={18} sx={{ color: accent }} />
          <Typography sx={{ fontWeight: 700, color: 'text.secondary' }}>Gemini is analysing your marketplace data…</Typography>
        </Stack>
        <LinearProgress sx={{ borderRadius: 1, bgcolor: alpha(accent, 0.12), '& .MuiLinearProgress-bar': { bgcolor: accent } }} />
      </Card>
    );
  }
  if (!ai) return null;

  const h = ai.headline || {};
  const tokens = ai.usage?.totalTokens;

  return (
    <Card
      sx={{
        p: { xs: 2, md: 3 }, mb: 2.5, borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(accent, 0.10)} 0%, ${alpha(accent, 0.02)} 100%)`,
        border: `1px solid ${alpha(accent, 0.3)}`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
        <Chip
          icon={<Sparkles size={14} />}
          label="AI SUGGESTIONS · GEMINI"
          size="small"
          sx={{ fontWeight: 800, letterSpacing: 0.5, bgcolor: alpha(accent, 0.16), color: accent, '& .MuiChip-icon': { color: accent } }}
        />
        {h.focus && (
          <Chip
            label={h.focus === 'buyer' ? 'Buyer acquisition' : h.focus === 'seed' ? 'Seed supply' : 'Seller acquisition'}
            size="small"
            sx={{ fontWeight: 700, bgcolor: alpha(accent, 0.10), color: accent }}
          />
        )}
      </Stack>

      {h.title && (
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.45rem' }, lineHeight: 1.25, mb: 1 }}>
          {h.title}
        </Typography>
      )}
      {h.message && (
        <Typography sx={{ fontSize: { xs: 14, md: 15 }, color: 'text.secondary', maxWidth: 820, lineHeight: 1.6, mb: 2 }}>
          {h.message}
        </Typography>
      )}

      {ai.marketInsight && (
        <Alert icon={<Scale size={18} />} severity="info" sx={{ mb: 2, borderRadius: 2.5, alignItems: 'center' }}>
          {ai.marketInsight}
        </Alert>
      )}

      {ai.quickWins?.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ color: accent, display: 'flex' }}><Zap size={16} /></Box>
            <Typography sx={{ fontWeight: 800, fontSize: 14 }}>Quick wins this week</Typography>
          </Stack>
          <Stack spacing={0.75}>
            {ai.quickWins.map((q, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: accent, minWidth: 16 }}>{i + 1}.</Typography>
                <Typography sx={{ fontSize: 13, color: 'text.primary', lineHeight: 1.5 }}>{q}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }} sx={gridItemSx}>
          <PlanColumn
            title="AI Seller Plan"
            subtitle="Gemini's pick of where to recruit sellers"
            icon={<Store size={22} />}
            accent={theme.palette.primary.main}
            items={ai.sellerPlan || []}
            type="seller"
            emptyText="Gemini found no seller gaps worth chasing."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={gridItemSx}>
          <PlanColumn
            title="AI Buyer Plan"
            subtitle="Gemini's pick of where to bring buyers"
            icon={<ShoppingCart size={22} />}
            accent={theme.palette.secondary.main}
            items={ai.buyerPlan || []}
            type="buyer"
            emptyText="Gemini found no buyer gaps worth chasing."
          />
        </Grid>
      </Grid>

      <Typography sx={{ mt: 2, fontSize: 11.5, fontWeight: 600, color: 'text.disabled', textAlign: 'center' }}>
        Generated {formatAgo(ai.generatedAt)}
        {ai.days ? ` · last ${ai.days} days` : ' · all time'}
        {ai.category ? ` · ${ai.category}` : ''}
        {ai.model ? ` · ${ai.model}` : ''}
        {tokens ? ` · ${tokens.toLocaleString()} tokens` : ''}
        {' · '}numbers come from your database, wording from Gemini
      </Typography>
    </Card>
  );
};

const MarketingStepsView = () => {
  const theme = useTheme();
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiConfigured, setAiConfigured] = useState(true);
  const [windowDays, setWindowDays] = useState(90);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const params = { days: windowDays };
      if (category) params.category = category;
      const res = await axios.get('/admin/marketing/next-steps', { params, signal });
      setData(res.data);
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      setError(err.response?.data?.msg || 'Failed to load marketing suggestions');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [windowDays, category]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Show the LAST stored AI result for this window × category. This is a plain
  // DB read — it never calls Gemini, so it costs no tokens.
  useEffect(() => {
    const controller = new AbortController();
    setAi(null);
    setAiError('');
    const params = { days: windowDays };
    if (category) params.category = category;
    axios.get('/admin/marketing/ai-suggestions', { params, signal: controller.signal, skipNotify: true })
      .then((res) => {
        setAi(res.data?.result || null);
        setAiConfigured(res.data?.configured !== false);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [windowDays, category]);

  // The ONLY place Gemini gets called: the admin clicks the button. Always fresh.
  const generateAi = async () => {
    if (aiLoading) return;
    setAiLoading(true);
    setAiError('');
    try {
      const res = await axios.post(
        '/admin/marketing/ai-suggestions',
        { days: windowDays, category: category || undefined },
        { skipNotify: true }
      );
      setAi(res.data?.result || null);
    } catch (err) {
      setAiError(err.response?.data?.msg || 'Could not generate AI suggestions');
    } finally {
      setAiLoading(false);
    }
  };

  const k = data?.kpis || {};
  const headline = data?.headline;
  const focusColor =
    headline?.focus === 'buyer' ? theme.palette.secondary.main
      : headline?.focus === 'seed' ? theme.palette.info.main
        : theme.palette.primary.main;

  const kpiTiles = [
    { label: 'Sellers', value: (k.totalSellers || 0).toLocaleString(), icon: <Briefcase size={20} />, color: theme.palette.primary.main, secondary: `+${k.newSellers30d || 0} in last 30d` },
    { label: 'Buyers', value: (k.totalBuyers || 0).toLocaleString(), icon: <UsersIcon size={20} />, color: theme.palette.secondary.main, secondary: `+${k.newBuyers30d || 0} in last 30d` },
    { label: 'Buyer : Seller', value: `${k.buyerSellerRatio ?? 0}:1`, icon: <Scale size={20} />, color: '#a855f7', secondary: 'marketplace balance' },
    { label: 'Products', value: (k.totalProducts || 0).toLocaleString(), icon: <ShoppingBag size={20} />, color: theme.palette.info.main, secondary: 'live supply' },
    { label: 'Businesses', value: (k.totalBusinesses || 0).toLocaleString(), icon: <Store size={20} />, color: '#f59e0b', secondary: 'registered sellers' },
    { label: 'Buyer Enquiries', value: (k.totalEnquiries || 0).toLocaleString(), icon: <MessageSquare size={20} />, color: theme.palette.error.main, secondary: windowDays ? `in last ${windowDays}d` : 'all time' },
  ];

  const maxCitySupply = Math.max(1, ...(data?.underservedCities || []).map((c) => c.supply));
  const maxCatSupply = Math.max(1, ...(data?.underservedCategories || []).map((c) => c.supply));

  // Full (unfiltered) category list from the backend — keeps every option
  // selectable even while a filter is active.
  const categoryOptions = data?.availableCategories || [];

  const categoryFilterControl = (
    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 170 }, width: { xs: '100%', sm: 'auto' } }}>
      <InputLabel id="mkt-category-label">Category</InputLabel>
      <Select
        labelId="mkt-category-label"
        label="Category"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        sx={{ borderRadius: 2, fontWeight: 600, bgcolor: 'background.paper' }}
      >
        <MenuItem value=""><em>All categories</em></MenuItem>
        {categoryOptions.map((c) => (
          <MenuItem key={c} value={c}>{c}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%' }}>
      <DashboardSectionHeader
        title="Next Marketing Steps"
        subtitle="Data-driven seller & buyer acquisition plan from your supply-vs-demand gaps."
        ranges={RANGES}
        rangeValue={windowDays}
        onRangeChange={setWindowDays}
        onRefresh={load}
        refreshTooltip="Re-run analysis"
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            {categoryFilterControl}
            <Button
              variant="contained"
              size="small"
              startIcon={aiLoading ? <CircularProgress size={14} color="inherit" /> : <Sparkles size={16} />}
              onClick={generateAi}
              disabled={aiLoading || !aiConfigured}
              title={aiConfigured ? 'Calls Gemini once with a summary of the data shown on this page' : 'GEMINI_API_KEY is not set on the server'}
              sx={{
                fontWeight: 700, height: 40, boxShadow: 'none',
                bgcolor: '#a855f7', '&:hover': { bgcolor: '#9333ea', boxShadow: 'none' },
              }}
            >
              {aiLoading ? 'Generating…' : ai ? 'Regenerate with AI' : 'Get AI suggestions'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon size={16} />}
              onClick={() => setSettingsOpen(true)}
              sx={{ borderColor: theme.palette.divider, color: 'text.secondary', fontWeight: 700, height: 40 }}
            >
              Settings
            </Button>
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError('')}>{error}</Alert>}
      {aiError && <Alert severity="warning" sx={{ mb: 2.5 }} onClose={() => setAiError('')}>{aiError}</Alert>}

      {/* Gemini AI suggestions (button-triggered only) */}
      <AiSuggestionsPanel ai={ai} loading={aiLoading} />

      {/* Headline recommendation */}
      {loading ? (
        <Skeleton variant="rounded" height={150} sx={{ borderRadius: 3, mb: 2.5 }} />
      ) : headline ? (
        <Card
          sx={{
            p: { xs: 2.5, md: 3.5 }, mb: 2.5, borderRadius: 3, position: 'relative', overflow: 'hidden',
            background: `linear-gradient(135deg, ${alpha(focusColor, 0.12)} 0%, ${alpha(focusColor, 0.03)} 100%)`,
            border: `1px solid ${alpha(focusColor, 0.25)}`,
          }}
        >
          <Box sx={{ position: 'absolute', top: -50, right: -30, opacity: 0.12, color: focusColor }}>
            <Target size={170} />
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
            <Chip
              icon={<Lightbulb size={14} />}
              label="THIS MONTH'S FOCUS"
              size="small"
              sx={{ fontWeight: 800, letterSpacing: 0.5, bgcolor: alpha(focusColor, 0.16), color: focusColor, '& .MuiChip-icon': { color: focusColor } }}
            />
            <Chip
              label={headline.focus === 'buyer' ? 'Buyer acquisition' : headline.focus === 'seed' ? 'Seed supply' : 'Seller acquisition'}
              size="small"
              sx={{ fontWeight: 700, bgcolor: alpha(focusColor, 0.10), color: focusColor }}
            />
          </Stack>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.3rem', md: '1.6rem' }, lineHeight: 1.2, mb: 1, position: 'relative' }}>
            {headline.title}
          </Typography>
          <Typography sx={{ fontSize: { xs: 14, md: 15 }, color: 'text.secondary', maxWidth: 760, lineHeight: 1.6, position: 'relative' }}>
            {headline.message}
          </Typography>
        </Card>
      ) : null}

      {/* Market balance insight */}
      {!loading && data?.marketInsight && (
        <Alert
          icon={<Scale size={18} />}
          severity="info"
          sx={{ mb: 2.5, borderRadius: 2.5, alignItems: 'center', fontWeight: 500 }}
        >
          {data.marketInsight}
        </Alert>
      )}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(loading ? Array.from({ length: 6 }) : kpiTiles).map((t, i) => (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={i} sx={gridItemSx}>
            {loading ? (
              <Skeleton variant="rounded" height={150} sx={{ borderRadius: 3, width: '100%' }} />
            ) : (
              <KpiTile label={t.label} value={t.value} icon={t.icon} color={t.color} secondary={t.secondary} />
            )}
          </Grid>
        ))}
      </Grid>

      {/* Seller & Buyer plans */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }} sx={gridItemSx}>
          {loading ? (
            <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3, width: '100%' }} />
          ) : (
            <PlanColumn
              title="Seller Acquisition Plan"
              subtitle="Where buyers are asking but supply is thin → recruit sellers"
              icon={<Store size={22} />}
              accent={theme.palette.primary.main}
              items={data?.sellerPlan || []}
              type="seller"
              emptyText="No clear seller gaps in this window."
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={gridItemSx}>
          {loading ? (
            <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3, width: '100%' }} />
          ) : (
            <PlanColumn
              title="Buyer Acquisition Plan"
              subtitle="Where supply is ready but buyers are few → attract buyers"
              icon={<ShoppingCart size={22} />}
              accent={theme.palette.secondary.main}
              items={data?.buyerPlan || []}
              type="buyer"
              emptyText="No clear buyer gaps in this window."
            />
          )}
        </Grid>
      </Grid>

      {/* Coverage gaps */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }} sx={gridItemSx}>
          {loading ? (
            <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3, width: '100%' }} />
          ) : (
            <CoverageList
              title="Underserved Cities"
              icon={<MapPin size={18} />}
              accent={theme.palette.primary.main}
              rows={data?.underservedCities || []}
              nameKey="city"
              maxSupply={maxCitySupply}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={gridItemSx}>
          {loading ? (
            <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3, width: '100%' }} />
          ) : (
            <CoverageList
              title="Underserved Categories"
              icon={<Tag size={18} />}
              accent={theme.palette.info.main}
              rows={data?.underservedCategories || []}
              nameKey="category"
              maxSupply={maxCatSupply}
            />
          )}
        </Grid>
      </Grid>

      {/* Trending buyer searches */}
      {!loading && (data?.trendingSearches?.length > 0) && (
        <Card sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, mb: 2 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
            <Box sx={{ color: theme.palette.secondary.main, display: 'flex' }}><Search size={18} /></Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>Trending Buyer Searches</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
            What buyers are looking for {windowDays ? `in the last ${windowDays} days` : 'all time'} — make sure these are well stocked.
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: 'nowrap',
              overflowX: 'auto',
              pb: 1,
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 3,
                bgcolor: alpha(theme.palette.secondary.main, 0.3),
              },
              scrollbarWidth: 'thin',
              scrollbarColor: `${alpha(theme.palette.secondary.main, 0.3)} transparent`,
            }}
          >
            {data.trendingSearches.map((s, i) => (
              <Chip
                key={`${s.keyword}-${i}`}
                icon={<TrendingUp size={13} />}
                label={`${s.keyword} · ${s.count}`}
                sx={{
                  flexShrink: 0,
                  fontWeight: 700, textTransform: 'capitalize',
                  bgcolor: alpha(theme.palette.secondary.main, 0.10),
                  color: theme.palette.secondary.dark,
                  '& .MuiChip-icon': { color: theme.palette.secondary.main },
                }}
              />
            ))}
          </Stack>
        </Card>
      )}

      {!loading && data && (
        <Stack spacing={0.5} alignItems="center" sx={{ mt: 1, mb: 2, color: 'text.disabled' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Sparkles size={13} />
            <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>
              Generated from live data · supply = products + businesses, demand = buyer enquiries
              {data.analysisWindowDays ? ` (last ${data.analysisWindowDays} days)` : ' (all time)'}
            </Typography>
          </Stack>
          {(data.settings?.seller_min_sellers > 0 || data.settings?.buyer_min_demand > 0) && (
            <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
              Filters active:
              {data.settings?.seller_min_sellers > 0 ? ` sellers ≥ ${data.settings.seller_min_sellers}` : ''}
              {data.settings?.seller_min_sellers > 0 && data.settings?.buyer_min_demand > 0 ? ' ·' : ''}
              {data.settings?.buyer_min_demand > 0 ? ` buyer demand ≥ ${data.settings.buyer_min_demand}` : ''}
            </Typography>
          )}
        </Stack>
      )}

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={data?.settings}
        onSaved={load}
      />
    </Box>
  );
};

export default MarketingStepsView;
